import React from "react";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import FadeUp from "@/components/brand/FadeUp";

const LAST_UPDATED = "June 8, 2026";

const LegalSection = ({ heading, children, testId }) => (
    <FadeUp>
        <section
            className="mb-8"
            data-testid={testId}
            style={{ scrollMarginTop: "6rem" }}
        >
            {heading && (
                <h2
                    className="vfs-h3 mb-4"
                    style={{ fontSize: "1.5rem", fontWeight: 600 }}
                >
                    {heading}
                </h2>
            )}
            <div
                className="text-base sm:text-[1.0625rem] leading-relaxed space-y-4"
                style={{ color: "var(--text-primary)" }}
            >
                {children}
            </div>
        </section>
    </FadeUp>
);

const OutlinedDisclaimer = ({ children, testId }) => (
    <div
        className="p-5 sm:p-6 rounded-2xl my-2"
        style={{
            background: "rgba(251, 191, 36, 0.06)",
            border: "1px solid rgba(251, 191, 36, 0.30)",
        }}
        data-testid={testId}
    >
        <p
            className="text-base leading-relaxed"
            style={{ color: "var(--text-primary)" }}
        >
            {children}
        </p>
    </div>
);

export default function Terms() {
    return (
        <>
            <SeoHead
                title="Terms & Participation Disclaimer | Vibe for Success"
                description="Terms of use and participation disclaimers for the Vibe for Success website and Future Builders Lab pilot program."
                path="/terms"
            />

            {/* Hero */}
            <section
                className="vfs-section relative overflow-hidden"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
                data-testid="terms-hero"
            >
                <GlowingGridBackground accent="#3B82F6" opacity={0.14} />
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <span className="vfs-eyebrow">
                                Terms & Disclaimer
                            </span>
                            <h1 className="vfs-h1 mt-5" data-testid="terms-h1">
                                Terms &amp; Participation Disclaimer
                            </h1>
                            <p
                                className="mt-4 text-sm"
                                style={{ color: "var(--text-secondary)" }}
                                data-testid="terms-last-updated"
                            >
                                Last updated: {LAST_UPDATED}.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Long-form content */}
            <section className="pb-24" data-testid="terms-content">
                <div className="vfs-container">
                    <GlassCard
                        className="p-7 sm:p-10 lg:p-12 max-w-3xl mx-auto"
                        hoverLift={false}
                    >
                        <LegalSection testId="terms-intro">
                            <p>
                                These terms describe how to use the Vibe for
                                Success website and what to expect from the
                                Future Builders Lab pilot program. By using
                                this website or submitting an interest form,
                                you agree to the items below.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Website use"
                            testId="terms-website-use"
                        >
                            <p>
                                This website provides general information
                                about Vibe for Success, Inc. and Future
                                Builders Lab. Information may be updated,
                                added, or removed as the nonprofit and its
                                programs evolve. Please do not submit any
                                information you do not want us to receive.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Pilot program status"
                            testId="terms-pilot-status"
                        >
                            <p>
                                Vibe for Success, Inc. is a new Florida
                                nonprofit corporation, and Future Builders Lab
                                is its first pilot program. Program details,
                                schedule, location, and dates are subject to
                                change as the pilot is developed. The site
                                will be updated as details are confirmed.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Program information may change"
                            testId="terms-program-changes"
                        >
                            <p>
                                Curriculum, format, and timing for Future
                                Builders Lab are described as a sample
                                structure and may be adjusted before or during
                                the pilot.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Participation disclaimer"
                            testId="terms-participation-disclaimer"
                        >
                            <OutlinedDisclaimer testId="terms-participation-block">
                                Future Builders Lab is an educational
                                workforce-readiness program. Participation
                                does not guarantee employment, income,
                                admission to any school or program, or
                                professional certification. The goal is to
                                help students build confidence, complete a
                                beginner-friendly digital project, and
                                practice safe use of AI-assisted web tools.
                            </OutlinedDisclaimer>
                        </LegalSection>

                        <LegalSection
                            heading="Parent / guardian approval"
                            testId="terms-parent-approval"
                        >
                            <p>
                                Parent or guardian approval is required for
                                student participation in Future Builders Lab.
                                Students may not participate until approval
                                has been confirmed.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Student privacy and safety"
                            testId="terms-student-privacy"
                        >
                            <OutlinedDisclaimer testId="terms-privacy-block">
                                Parent or guardian approval is required for
                                student participation. Student websites are
                                private or unlisted by default unless a parent
                                or guardian chooses otherwise. Students are
                                guided not to publish sensitive personal
                                information, private contact details, exact
                                addresses, school schedules, or other unsafe
                                identifying information.
                            </OutlinedDisclaimer>
                        </LegalSection>

                        <LegalSection
                            heading="Sponsorship and tax status"
                            testId="terms-sponsorship-status"
                        >
                            <OutlinedDisclaimer testId="terms-sponsorship-block">
                                Vibe for Success, Inc. is a Florida nonprofit
                                corporation. Sponsorships and program support
                                help fund pilot program development and
                                student participation. Tax-deductible donation
                                status should not be assumed unless confirmed
                                in writing by Vibe for Success, Inc.
                            </OutlinedDisclaimer>
                        </LegalSection>

                        <LegalSection
                            heading="Third-party tools and resources"
                            testId="terms-third-party"
                        >
                            <p>
                                Future Builders Lab may introduce students to
                                third-party tools (for example, Emergent,
                                Canva, and browser-based web tools). Vibe for
                                Success, Inc. does not control these tools,
                                and their availability or terms may change
                                over time.
                            </p>
                        </LegalSection>

                        <LegalSection
                            heading="Changes to these terms"
                            testId="terms-changes"
                        >
                            <p>
                                We may update these terms as the nonprofit and
                                its programs grow. When we make changes, we
                                will update the &ldquo;Last updated&rdquo;
                                date at the top of this page.
                            </p>
                        </LegalSection>

                        <LegalSection heading="Contact" testId="terms-contact">
                            <p>
                                Questions about these terms can be sent to{" "}
                                <a
                                    href="mailto:hello@vibeforsuccess.org"
                                    className="underline"
                                    style={{
                                        color: "var(--accent-yellow)",
                                    }}
                                    data-testid="terms-contact-email"
                                >
                                    hello@vibeforsuccess.org
                                </a>
                                .
                            </p>
                        </LegalSection>
                    </GlassCard>
                </div>
            </section>
        </>
    );
}
